import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@sshop/auth';
import { AppError } from '@sshop/api-client';
import { useTranslation } from '@sshop/i18n';
import { Button, Input, Modal } from '@sshop/ui';

import { useLocale } from '@/shared/hooks/useLocale';
import { routes } from '@/shared/lib/routes';
import { SocialAuthButtons } from './SocialAuthButtons/SocialAuthButtons';
import { RegistrationFields } from './RegistrationFields';
import {
  isValidRegistrationEmail,
  registrationErrors,
  registrationFieldOrder,
  type RegistrationField,
} from './registrationValidation';

type CheckoutAuthContextValue = { requireAuth: (action: () => void) => void };
const CheckoutAuthContext = createContext<CheckoutAuthContextValue | null>(null);

export function useCheckoutAuth() {
  const context = useContext(CheckoutAuthContext);
  if (!context) throw new Error('useCheckoutAuth must be used inside CheckoutAuthProvider');
  return context;
}

export function CheckoutAuthProvider({ children }: { children: ReactNode }) {
  const { status, session, provider } = useAuth();
  const { t } = useTranslation('auth');
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phone, setPhone] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<RegistrationField, boolean>>>({});
  const [serverErrors, setServerErrors] = useState<Partial<Record<RegistrationField, string>>>({});
  const [resetSent, setResetSent] = useState(false);
  const registering = mode === 'register';
  const values = { name, surname, phone, idNumber, email, password, passwordRepeat, agree };
  const fieldErrors = registrationErrors(values, t);
  const visibleErrors = Object.fromEntries(
    registrationFieldOrder
      .filter((key) => attempted || touched[key])
      .map((key) => [key, serverErrors[key] ?? fieldErrors[key]]),
  );
  const setField = (key: RegistrationField, value: string) => {
    setServerErrors((previous) => ({ ...previous, [key]: undefined }));
    (
      ({
        name: setName,
        surname: setSurname,
        phone: setPhone,
        idNumber: setIdNumber,
        email: setEmail,
        password: setPassword,
        passwordRepeat: setPasswordRepeat,
      }) as Partial<Record<RegistrationField, (value: string) => void>>
    )[key]?.(value);
  };
  const emailError =
    (attempted || touched.email) && !isValidRegistrationEmail(email)
      ? t('emailInvalid')
      : undefined;
  const passwordError =
    mode === 'login' && (attempted || touched.password) && !password.trim()
      ? t('requiredField')
      : undefined;

  const requireAuth = useCallback(
    (action: () => void) => {
      if (status === 'authenticated' && session) {
        action();
        return;
      }
      setMode('login');
      setError(null);
      setAttempted(false);
      setTouched({});
      setResetSent(false);
      setPendingAction(() => action);
    },
    [status, session],
  );

  useEffect(() => {
    if (status !== 'authenticated' || !session || !pendingAction || busy) return;
    setPendingAction(null);
    pendingAction();
  }, [status, session, pendingAction, busy]);

  // A direct visit to /checkout lands on the cart with the same dismissible prompt.
  useEffect(() => {
    if (!(location.state as { openCheckoutAuth?: boolean } | null)?.openCheckoutAuth) return;
    if (status === 'unknown') return;
    if (status === 'authenticated' && session) {
      navigate(routes.checkout(locale), { replace: true });
      return;
    }
    setMode('login');
    setError(null);
    setAttempted(false);
    setTouched({});
    setResetSent(false);
    setPendingAction(() => () => navigate(routes.checkout(locale)));
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, locale, navigate, session, status]);

  const close = () => {
    if (busy) return;
    setPendingAction(null);
    setError(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setAttempted(true);
    setError(null);
    if (registering) {
      const firstInvalid = registrationFieldOrder.find((key) => fieldErrors[key]);
      if (firstInvalid) {
        document.getElementById(`checkout-register-${firstInvalid}`)?.focus();
        return;
      }
    } else {
      if (!isValidRegistrationEmail(email)) {
        document.getElementById('checkout-auth-email')?.focus();
        return;
      }
      if (mode === 'login' && !password.trim()) {
        document.getElementById('checkout-auth-password')?.focus();
        return;
      }
    }
    if (mode === 'forgot') {
      setBusy(true);
      try {
        await provider.forgotPassword({ email: email.trim() });
        setResetSent(true);
      } catch {
        setError(t('resetLinkFailed'));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      if (mode === 'login') {
        await provider.login({ email: email.trim(), password });
      } else {
        await provider.register({
          name: name.trim(),
          surname: surname.trim(),
          phone,
          idNumber,
          email: email.trim(),
          password,
          passwordRepeat,
          agree,
        });
      }
      const action = pendingAction;
      setPendingAction(null);
      setPassword('');
      setPasswordRepeat('');
      action?.();
    } catch (cause) {
      const errors =
        cause instanceof AppError
          ? (cause.details?.['errors'] as Record<string, unknown> | undefined)
          : undefined;
      if (registering && errors?.['email']) {
        setServerErrors({ email: t('emailTaken') });
        document.getElementById('checkout-register-email')?.focus();
      } else {
        setError(t(mode === 'login' ? 'invalidCredentials' : 'registerFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <CheckoutAuthContext.Provider value={{ requireAuth }}>
      {children}
      <Modal
        open={pendingAction !== null && status !== 'unknown'}
        onClose={close}
        title={t(mode === 'login' ? 'signIn' : mode === 'register' ? 'signUp' : 'forgotPassword')}
        description={t(mode === 'forgot' ? 'forgotPasswordHint' : 'checkoutPrompt')}
        closeLabel={t('close')}
        size={registering ? 'xl' : 'md'}
        className="max-h-[calc(100dvh-2rem)]"
        bodyClassName={registering ? 'scrollbar-none py-3' : ''}
        footer={
          registering ? (
            <Button
              type="submit"
              form="checkout-auth-form"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={busy}
            >
              {busy ? t('signingUp') : t('createAccount')}
            </Button>
          ) : undefined
        }
        closeOnBackdrop={!busy}
        closeOnEscape={!busy}
      >
        <form
          id="checkout-auth-form"
          className={`flex flex-col ${registering ? 'gap-3' : 'gap-4'}`}
          onSubmit={(event) => void submit(event)}
          noValidate
        >
          {registering ? (
            <RegistrationFields
              values={values}
              errors={visibleErrors}
              onChange={setField}
              onBlur={(key) => setTouched((previous) => ({ ...previous, [key]: true }))}
              t={t}
              busy={busy}
              compact
              idPrefix="checkout-register"
            />
          ) : (
            <>
              <Input
                id="checkout-auth-email"
                label={t('email')}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setTouched((previous) => ({ ...previous, email: true }))}
                error={emailError}
                required
                disabled={busy || resetSent}
                autoComplete="email"
              />
              {mode !== 'forgot' && (
                <Input
                  id="checkout-auth-password"
                  label={t('password')}
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onBlur={() => setTouched((previous) => ({ ...previous, password: true }))}
                  error={passwordError}
                  required
                  disabled={busy}
                  autoComplete="current-password"
                />
              )}
            </>
          )}
          {mode === 'login' && (
            <button
              type="button"
              className="text-accent self-end text-sm hover:underline"
              onClick={() => {
                setMode('forgot');
                setError(null);
                setAttempted(false);
                setTouched({});
              }}
            >
              {t('forgotPassword')}
            </button>
          )}
          {registering && (
            <label
              className={`border-border-subtle bg-bg-subtle/50 text-fg-secondary hover:border-border-strong flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${attempted && !agree ? 'border-danger' : ''}`}
            >
              <input
                type="checkbox"
                className="peer sr-only"
                checked={agree}
                onChange={(event) => setAgree(event.target.checked)}
                onBlur={() => setTouched((previous) => ({ ...previous, agree: true }))}
                id="checkout-register-agree"
                aria-invalid={attempted && !agree ? true : undefined}
                disabled={busy}
              />
              <span
                aria-hidden="true"
                className="border-border-strong bg-bg-canvas peer-checked:border-accent peer-checked:bg-accent peer-focus-visible:ring-accent mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-checked:[&>svg]:opacity-100"
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="h-3.5 w-3.5 text-white opacity-0 transition-opacity"
                  aria-hidden="true"
                >
                  <path
                    d="m3 8 3.2 3.2L13 4.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                {t('agreePrefix')}{' '}
                <Link
                  to={routes.termsConditions(locale)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent font-medium hover:underline"
                >
                  {t('termsLink')}
                </Link>
              </span>
            </label>
          )}
          {registering && visibleErrors.agree && (
            <p role="alert" className="text-danger-default text-sm">
              {visibleErrors.agree}
            </p>
          )}
          {error && (
            <p role="alert" className="text-danger-default text-sm">
              {error}
            </p>
          )}
          {resetSent && (
            <p role="status" className="text-sm">
              {t('resetLinkSent')}
            </p>
          )}
          {!registering && (
            <Button type="submit" variant="primary" size="lg" disabled={busy || resetSent}>
              {t(
                mode === 'forgot'
                  ? busy
                    ? 'sending'
                    : 'sendResetLink'
                  : busy
                    ? registering
                      ? 'signingUp'
                      : 'signingIn'
                    : registering
                      ? 'signUp'
                      : 'signIn',
              )}
            </Button>
          )}
          {mode !== 'forgot' && (
            <>
              <div className="text-fg-muted text-center text-xs uppercase">
                {t('orContinueWith')}
              </div>
              <SocialAuthButtons
                busy={busy}
                returnTo={`${location.pathname}${location.search}${location.hash}`}
              />
            </>
          )}
          <button
            type="button"
            className="text-accent text-sm hover:underline"
            disabled={busy}
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
              setAttempted(false);
              setTouched({});
              setResetSent(false);
            }}
          >
            {mode === 'forgot' ? (
              t('backToLogin')
            ) : (
              <>
                {t(mode === 'login' ? 'noAccount' : 'haveAccount')}{' '}
                {t(mode === 'login' ? 'signUp' : 'signIn')}
              </>
            )}
          </button>
        </form>
      </Modal>
    </CheckoutAuthContext.Provider>
  );
}
